import React, { useState, useRef, useEffect } from 'react';
import SearchIcon from '@mui/icons-material/Search';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import CloseIcon from '@mui/icons-material/Close';


const ClassBoxSearch = () => {
    const [searchTerm, setSearchTerm] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [currentMatchIndex, setCurrentMatchIndex] = useState(-1);
    const [isSearchActive, setIsSearchActive] = useState(false);
    
    const searchInputRef = useRef(null);
    
    // Store original content to restore later
    // const [originalContent, setOriginalContent] = useState(new Map());

    // const updateOriginalContent = () => {
    //     const classBoxElements = document.querySelectorAll('.class-box');
    //     const contentMap = new Map();
        
    //     classBoxElements.forEach((element, index) => {
    //         // Use a unique identifier for each element to handle dynamic changes
    //         const elementId = element.dataset.searchId || `element-${index}-${Date.now()}`;
    //         element.dataset.searchId = elementId;
    //         contentMap.set(elementId, element.innerHTML);
    //     });
        
    //     setOriginalContent(contentMap);
    // };

    // useEffect(() => {
    //     updateOriginalContent();
    // }, []);

    const clearHighlights = () => {
        const classBoxElements = document.querySelectorAll('.class-box');
        classBoxElements.forEach((element) => {
            // const elementId = element.dataset.searchId;
            // if (elementId && originalContent.has(elementId)) {
            //     element.innerHTML = originalContent.get(elementId);
            // } else {

            // If element doesn't have stored content, remove highlights manually
            const highlights = element.querySelectorAll('.search-highlight, .search-highlight-active');
            highlights.forEach(highlight => {
                const parent = highlight.parentNode;
                parent.replaceChild(document.createTextNode(highlight.textContent), highlight);
                parent.normalize();
            });

            // }
        });
    };

    const highlightMatches = (element, term, elementIndex) => {
        if (!element || !term) return [];
        
        const walker = document.createTreeWalker(
            element,
            NodeFilter.SHOW_TEXT,
            null,
            false
        );

        const textNodes = [];
        let node;
        while (node = walker.nextNode()) {
            textNodes.push(node);
        }

        const matches = [];
        // exact match regex to avoid partial matches
        const regex = new RegExp(`^(${term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})$`, 'gi');

        // contain words regex
        // const regex = new RegExp(`(${term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');

        textNodes.forEach((textNode, nodeIndex) => {
            const text = textNode.textContent;
            const matchArray = [...text.matchAll(regex)];
            
            if (matchArray.length > 0) {
                const parent = textNode.parentNode;
                const parts = text.split(regex);
                const fragment = document.createDocumentFragment();
                
                let matchCount = 0;
                parts.forEach((part, index) => {
                    if (index % 2 === 0) {
                        // Regular text
                        if (part) fragment.appendChild(document.createTextNode(part));
                    } else {
                        // Matched text
                        const mark = document.createElement('mark');
                        mark.textContent = part;
                        mark.className = 'search-highlight';
                        mark.dataset.matchId = `match-${elementIndex}-${nodeIndex}-${matchCount}`;
                        mark.dataset.elementIndex = elementIndex;
                        fragment.appendChild(mark);
                        
                        matches.push({
                            element: mark,
                            text: part,
                            elementIndex,
                            matchId: `match-${elementIndex}-${nodeIndex}-${matchCount}`
                        });
                        matchCount++;
                    }
                });
                
                parent.replaceChild(fragment, textNode);
            }
        });

        return matches;
    };

    const performSearch = (term) => {
        if (!term.trim()) {
            clearHighlights();
            setSearchResults([]);
            setCurrentMatchIndex(-1);
            return;
        }

        // Update original content before searching to handle dynamic changes
        // updateOriginalContent();
        clearHighlights();
        
        const classBoxElements = document.querySelectorAll('.class-box');
        let allMatches = [];
        
        classBoxElements.forEach((element, index) => {
            const matches = highlightMatches(element, term, index);
            allMatches = [...allMatches, ...matches];
        });

        setSearchResults(allMatches);
        setCurrentMatchIndex(allMatches.length > 0 ? 0 : -1);
        
        if (allMatches.length > 0) {
            scrollToMatch(allMatches[0]);
        }
    };

    const scrollToMatch = (match) => {
        if (match && match.element) {
            // Remove previous active highlight
            document.querySelectorAll('.search-highlight-active').forEach(el => {
                el.classList.remove('search-highlight-active');
            });
            
            // Add active highlight to current match
            match.element.classList.add('search-highlight-active');
            
            // Scroll to the match
            match.element.scrollIntoView({
                behavior: 'smooth',
                block: 'center',
                inline: 'center'
            });
        }
    };

    const navigateToMatch = (direction) => {
        // Re-validate search results in case elements were removed
        const validMatches = searchResults.filter(match => 
            document.contains(match.element)
        );
        
        if (validMatches.length === 0) {
            // If no valid matches, re-run search to get fresh results
            performSearch(searchTerm);
            return;
        }
        
        // Update search results if some matches were removed
        if (validMatches.length !== searchResults.length) {
            setSearchResults(validMatches);
            setCurrentMatchIndex(Math.min(currentMatchIndex, validMatches.length - 1));
        }
        
        let newIndex;
        if (direction === 'next') {
            newIndex = currentMatchIndex < validMatches.length - 1 ? currentMatchIndex + 1 : 0;
        } else {
            newIndex = currentMatchIndex > 0 ? currentMatchIndex - 1 : validMatches.length - 1;
        }
        
        setCurrentMatchIndex(newIndex);
        scrollToMatch(validMatches[newIndex]);
    };

    const handleSearchChange = (e) => {
        const value = e.target.value;
        setSearchTerm(value);
        performSearch(value);
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            if (e.shiftKey) {
                navigateToMatch('prev');
            } else {
                navigateToMatch('next');
            }
            e.preventDefault();
        } else if (e.key === 'Escape') {
            closeSearch();
        }
    };

    const openSearch = () => {
        // Update content when opening search to catch any changes
        // updateOriginalContent();
        setIsSearchActive(true);
        setTimeout(() => searchInputRef.current?.focus(), 100);
    };

    const closeSearch = () => {
        setIsSearchActive(false);
        setSearchTerm('');
        setSearchResults([]);
        setCurrentMatchIndex(-1);
        clearHighlights();
    };

    // Keyboard shortcut for Ctrl+F and observe DOM changes
    useEffect(() => {
        const handleKeyDown = (e) => {
            if ((e.ctrlKey || e.metaKey) && e.shiftKey && (e.key === 'f' || e.key === 'F')) {
                e.preventDefault();
                openSearch();
            }
        };

        // Set up MutationObserver to watch for class-box changes
        const observer = new MutationObserver((mutations) => {
            let shouldUpdate = false;
        
            mutations.forEach((mutation) => {
                // Check if class-box elements were added or removed
                if (mutation.type === 'childList') {
                    const addedNodes = Array.from(mutation.addedNodes);
                    const removedNodes = Array.from(mutation.removedNodes);
                    
                    const hasClassBoxChanges = [...addedNodes, ...removedNodes].some(node => 
                        node.nodeType === 1 && (
                            node.classList?.contains('class-box') || 
                            node.querySelector?.('.class-box')
                        )
                    );
                    
                    if (hasClassBoxChanges) {
                        shouldUpdate = true;
                    }
                }
                
                // Check if class-box content was modified
                if (mutation.type === 'childList' || mutation.type === 'characterData') {
                    const target = mutation.target;
                    if (target.classList?.contains('class-box') || target.closest?.('.class-box')) {
                        shouldUpdate = true;
                    }
                }
            });
        
            if (shouldUpdate && isSearchActive && searchTerm) {
                // Re-run search with current term to update results
                setTimeout(() => performSearch(searchTerm), 100);
            }
        });

        // Observe the entire document for changes
        observer.observe(document.body, {
            childList: true,
            subtree: true,
            characterData: true
        });

        document.addEventListener('keydown', handleKeyDown);
        
        return () => {
            observer.disconnect();
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [isSearchActive, searchTerm]); // Add dependencies to re-create observer when needed

    return (
        <div style={{ position: 'relative' }}>
            {/* Custom Search Bar */}
            {isSearchActive && (
                <div style={{
                    position: 'fixed',
                    top: '16px',
                    right: '16px',
                    zIndex: 50,
                    backgroundColor: 'white',
                    borderRadius: '8px',
                    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                    border: '1px solid #d1d5db',
                    padding: '12px',
                    minWidth: '320px'
                }}>
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                    }}>
                        <SearchIcon 
                            size={16} 
                            style={{ color: '#6b7280' }}
                        />
                        <input
                            ref={searchInputRef}
                            type="text"
                            value={searchTerm}
                            onChange={handleSearchChange}
                            onKeyDown={handleKeyDown}
                            placeholder="Search in class-box divs..."
                            style={{
                                flex: 1,
                                outline: 'none',
                                fontSize: '14px',
                                border: 'none',
                                backgroundColor: 'transparent'
                            }}
                        />
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px'
                        }}>
                            {searchResults.length > 0 && (
                                <>
                                    <span style={{
                                        fontSize: '12px',
                                        color: '#4b5563',
                                        marginRight: '8px'
                                    }}>
                                        {currentMatchIndex + 1} of {searchResults.length}
                                    </span>
                                    <button
                                        onClick={() => navigateToMatch('prev')}
                                        style={{
                                            padding: '4px',
                                            borderRadius: '4px',
                                            border: 'none',
                                            backgroundColor: 'transparent',
                                            cursor: 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                        }}
                                        onMouseEnter={(e) => e.target.style.backgroundColor = '#f3f4f6'}
                                        onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                                        title="Previous match (Shift+Enter)"
                                    >
                                        <KeyboardArrowUpIcon size={14} />
                                    </button>
                                    <button
                                        onClick={() => navigateToMatch('next')}
                                        style={{
                                            padding: '4px',
                                            borderRadius: '4px',
                                            border: 'none',
                                            backgroundColor: 'transparent',
                                            cursor: 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                        }}
                                        onMouseEnter={(e) => e.target.style.backgroundColor = '#f3f4f6'}
                                        onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                                        title="Next match (Enter)"
                                    >
                                        <KeyboardArrowDownIcon size={14} />
                                    </button>
                                </>
                            )}
                            <button
                                onClick={closeSearch}
                                style={{
                                    padding: '4px',
                                    borderRadius: '4px',
                                    border: 'none',
                                    backgroundColor: 'transparent',
                                    cursor: 'pointer',
                                    marginLeft: '4px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}
                                onMouseEnter={(e) => e.target.style.backgroundColor = '#f3f4f6'}
                                onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                                title="Close search (Escape)"
                            >
                                <CloseIcon size={14} />
                            </button>
                        </div>
                    </div>
                    {searchTerm && searchResults.length === 0 && (
                        <div style={{
                            fontSize: '12px',
                            color: '#dc2626',
                            marginTop: '4px'
                        }}>
                            No matches found in class-box elements
                        </div>
                    )}
                </div>
            )}

            {/* Styles for search highlighting */}
            <style>{`
                .search-highlight {
                    background-color: #ffeb3b;
                    padding: 1px 2px;
                    border-radius: 2px;
                }
                .search-highlight-active {
                    background-color: #ff9800 !important;
                    color: white;
                }
            `}</style>
        </div>
    );
};

export default ClassBoxSearch;